import React, { useState, useEffect } from 'react';
import { 
    Building2, Users, Package, DollarSign, TrendingUp, TrendingDown,
    Activity, Shield, Settings, Search, Filter, Plus, MoreVertical,
    ChevronRight, Eye, Edit, Trash2, RefreshCw, BarChart3, Clock,
    CheckCircle, XCircle, AlertCircle, Calendar, Loader2, X
} from 'lucide-react';
import axios from 'axios';
import CompanyWizard from '../components/admin/CompanyWizard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminPanel = () => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [modules, setModules] = useState([]);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCompany, setSelectedCompany] = useState(null);
    
    // Modal states
    const [showCompanyWizard, setShowCompanyWizard] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState('');
    
    // User form
    const [userForm, setUserForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        company_id: '',
        role_id: ''
    });
    
    useEffect(() => {
        fetchData();
    }, []);
    
    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsRes, companiesRes, modulesRes, usersRes, rolesRes] = await Promise.all([
                axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`),
                axios.get(`${BACKEND_URL}/api/admin/companies`),
                axios.get(`${BACKEND_URL}/api/admin/modules`),
                axios.get(`${BACKEND_URL}/api/admin/users`),
                axios.get(`${BACKEND_URL}/api/admin/roles`)
            ]);
            
            if (statsRes.data.success) setStats(statsRes.data.stats);
            if (companiesRes.data.success) setCompanies(companiesRes.data.companies);
            if (modulesRes.data.success) setModules(modulesRes.data.modules);
            if (usersRes.data.success) setUsers(usersRes.data.users);
            if (rolesRes.data.success) setRoles(rolesRes.data.roles);
        } catch (err) {
            console.error('Error fetching admin data:', err);
        }
        setLoading(false);
    };
    
    // Create User
    const handleCreateUser = async (e) => {
        e.preventDefault();
        setModalLoading(true);
        setModalError('');
        
        try {
            const res = await axios.post(`${BACKEND_URL}/api/admin/users`, userForm);
            if (res.data.success) {
                setShowUserModal(false);
                setUserForm({
                    first_name: '',
                    last_name: '',
                    email: '',
                    password: '',
                    company_id: '',
                    role_id: ''
                });
                fetchData(); // Refresh data
            } else {
                setModalError(res.data.detail || 'Failed to create user');
            }
        } catch (err) {
            setModalError(err.response?.data?.detail || 'Error creating user');
        }
        setModalLoading(false);
    };
    
    const getStatusColor = (status) => {
        const colors = {
            active: 'bg-emerald-500/20 text-emerald-400',
            pending: 'bg-amber-500/20 text-amber-400',
            trial: 'bg-blue-500/20 text-blue-400',
            cancelled: 'bg-red-500/20 text-red-400',
            past_due: 'bg-orange-500/20 text-orange-400'
        };
        return colors[status] || 'bg-slate-500/20 text-slate-400';
    };
    
    const tabs = [
        { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
        { id: 'companies', label: 'Companies', icon: Building2 },
        { id: 'users', label: 'Users', icon: Users },
        { id: 'modules', label: 'Modules', icon: Package },
        { id: 'roles', label: 'Roles', icon: Shield },
    ];
    
    return (
        <div className="min-h-screen bg-slate-100 dark:bg-slate-900" data-testid="admin-panel">
            {/* Header */}
            <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <div className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <h1 className="text-xl font-bold text-slate-900 dark:text-white" data-testid="admin-panel-title">
                            shipez<span className="text-teal-500 dark:text-teal-400">.ai</span>
                            <span className="text-sm font-normal text-slate-500 dark:text-slate-400 ml-2">Admin Panel</span>
                        </h1>
                    </div>
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={fetchData}
                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            data-testid="admin-refresh-button"
                        >
                            <RefreshCw className={`w-5 h-5 text-slate-500 dark:text-slate-400 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg" data-testid="admin-settings-button">
                            <Settings className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                        </button>
                    </div>
                </div>
                
                {/* Tabs */}
                <div className="px-6 flex gap-1" data-testid="admin-tabs-container">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            data-testid={`admin-tab-${tab.id}`}
                            className={`px-4 py-3 flex items-center gap-2 text-sm font-medium transition-colors border-b-2 ${
                                activeTab === tab.id
                                    ? 'text-teal-600 dark:text-teal-400 border-teal-600 dark:border-teal-400'
                                    : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-white'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>
            
            {/* Content */}
            <div className="p-6" data-testid="admin-content">
                {loading ? (
                    <div className="flex items-center justify-center py-20" data-testid="admin-loading">
                        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Dashboard Tab */}
                        {activeTab === 'dashboard' && stats && (
                            <div className="space-y-6" data-testid="admin-dashboard-tab">
                                {/* Stats Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-grid">
                                    <StatCard
                                        title="Total Companies"
                                        value={stats.total_companies}
                                        icon={Building2}
                                        trend={stats.new_companies_this_month}
                                        trendLabel="new this month"
                                        color="teal"
                                        testId="stat-total-companies"
                                        date={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    />
                                    <StatCard
                                        title="Total Users"
                                        value={stats.total_users}
                                        icon={Users}
                                        color="blue"
                                        testId="stat-total-users"
                                        date={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    />
                                    <StatCard
                                        title="Active Subscriptions"
                                        value={stats.companies_by_status?.active || 0}
                                        icon={CheckCircle}
                                        color="emerald"
                                        testId="stat-active-subscriptions"
                                        date={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    />
                                    <StatCard
                                        title="Pending Activations"
                                        value={stats.companies_by_status?.pending || 0}
                                        icon={Clock}
                                        color="amber"
                                        testId="stat-pending-activations"
                                        date={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    />
                                </div>
                                
                                <div className="grid lg:grid-cols-2 gap-6">
                                    {/* Companies by Type */}
                                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6" data-testid="companies-by-type-chart">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Companies by Type</h3>
                                            <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </span>
                                        </div>
                                        <div className="space-y-3">
                                            {Object.entries(stats.companies_by_type || {}).map(([type, count]) => (
                                                <div key={type} className="flex items-center justify-between">
                                                    <span className="text-slate-600 dark:text-slate-300">{type}</span>
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-32 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                                            <div 
                                                                className="h-full bg-teal-500 rounded-full"
                                                                style={{ width: `${(count / stats.total_companies) * 100}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-slate-900 dark:text-white font-medium w-8 text-right">{count}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Module Subscriptions */}
                                    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Module Subscriptions</h3>
                                            <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </span>
                                        </div>
                                        <div className="space-y-3">
                                            {stats.module_subscriptions?.slice(0, 6).map(module => (
                                                <div key={module.module_code} className="flex items-center justify-between">
                                                    <span className="text-slate-600 dark:text-slate-300 text-sm">{module.module_name}</span>
                                                    <span className="px-2 py-1 bg-teal-500/20 text-teal-600 dark:text-teal-400 text-xs font-medium rounded">
                                                        {module.subscriptions} active
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Recent Companies */}
                                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Recent Companies</h3>
                                            <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </span>
                                        </div>
                                        <button 
                                            onClick={() => setActiveTab('companies')}
                                            className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 flex items-center gap-1"
                                        >
                                            View All <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <div className="divide-y divide-slate-200 dark:divide-slate-700">
                                        {companies.slice(0, 5).map(company => (
                                            <div key={company.id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/30">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                                                        <Building2 className="w-5 h-5 text-teal-500 dark:text-teal-400" />
                                                    </div>
                                                    <div>
                                                        <p className="text-slate-900 dark:text-white font-medium">{company.company_name}</p>
                                                        <p className="text-sm text-slate-500 dark:text-slate-400">{company.company_type} • {company.email}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className="text-xs text-slate-400 dark:text-slate-500">
                                                        {company.created_at ? new Date(company.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                                                    </span>
                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(company.subscription_status)}`}>
                                                        {company.subscription_status}
                                                    </span>
                                                    <span className="text-sm text-slate-500 dark:text-slate-400">
                                                        {company.module_count || 0} modules
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Companies Tab */}
                        {activeTab === 'companies' && (
                            <div className="space-y-4">
                                {/* Search & Actions */}
                                <div className="flex items-center justify-between">
                                    <div className="relative w-80">
                                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search companies..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        />
                                    </div>
                                    <button 
                                        onClick={() => setShowCompanyWizard(true)}
                                        data-testid="add-company-button"
                                        className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" /> Add Company
                                    </button>
                                </div>
                                
                                {/* Companies Table */}
                                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-slate-100 dark:bg-slate-700/50">
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Company</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Type</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Status</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Modules</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Users</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                            {companies
                                                .filter(c => c.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
                                                .map(company => (
                                                <tr key={company.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                                                    <td className="px-6 py-4">
                                                        <div>
                                                            <p className="text-slate-900 dark:text-white font-medium">{company.company_name}</p>
                                                            <p className="text-sm text-slate-500 dark:text-slate-400">{company.company_code}</p>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{company.company_type}</td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(company.subscription_status)}`}>
                                                            {company.subscription_status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{company.module_count || 0}</td>
                                                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{company.user_count || 0}</td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors">
                                                                <Eye className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                                                            </button>
                                                            <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors">
                                                                <Edit className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        
                        {/* Users Tab */}
                        {activeTab === 'users' && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="relative w-80">
                                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search users..."
                                            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        />
                                    </div>
                                    <button 
                                        onClick={() => {
                                            setModalError('');
                                            setShowUserModal(true);
                                        }}
                                        data-testid="add-user-button"
                                        className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" /> Add User
                                    </button>
                                </div>
                                
                                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-slate-100 dark:bg-slate-700/50">
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">User</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Company</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Role</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Status</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Last Login</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                            {users.map(user => (
                                                <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 bg-teal-500/20 rounded-full flex items-center justify-center">
                                                                <span className="text-teal-600 dark:text-teal-400 font-medium text-sm">
                                                                    {user.first_name?.[0]}{user.last_name?.[0]}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-900 dark:text-white font-medium">{user.first_name} {user.last_name}</p>
                                                                <p className="text-sm text-slate-500 dark:text-slate-400">{user.email}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">{user.company_name || '-'}</td>
                                                    <td className="px-6 py-4">
                                                        <span className="px-2 py-1 bg-purple-500/20 text-purple-600 dark:text-purple-400 text-xs font-medium rounded">
                                                            {user.role_name || 'No Role'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {user.is_active ? (
                                                            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-sm">
                                                                <CheckCircle className="w-4 h-4" /> Active
                                                            </span>
                                                        ) : (
                                                            <span className="flex items-center gap-1 text-red-500 dark:text-red-400 text-sm">
                                                                <XCircle className="w-4 h-4" /> Inactive
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-slate-500 dark:text-slate-400 text-sm">
                                                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors">
                                                                <Edit className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                                                            </button>
                                                            <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors">
                                                                <Trash2 className="w-4 h-4 text-red-500 dark:text-red-400" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        
                        {/* Modules Tab */}
                        {activeTab === 'modules' && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Module Catalog & Pricing</h2>
                                    <button className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors">
                                        <Plus className="w-4 h-4" /> Add Module
                                    </button>
                                </div>
                                
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {modules.map(module => (
                                        <div key={module.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center">
                                                    <Package className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                                                </div>
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${module.is_active ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' : 'bg-red-500/20 text-red-500 dark:text-red-400'}`}>
                                                    {module.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{module.module_name}</h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 line-clamp-2">{module.description}</p>
                                            
                                            <div className="space-y-2 mb-4">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-500 dark:text-slate-400">Base Price</span>
                                                    <span className="text-slate-900 dark:text-white font-medium">${module.base_price}/mo</span>
                                                </div>
                                                {module.per_seat_price > 0 && (
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-slate-500 dark:text-slate-400">Per Seat</span>
                                                        <span className="text-slate-900 dark:text-white font-medium">${module.per_seat_price}/seat</span>
                                                    </div>
                                                )}
                                                {module.usage_rate > 0 && (
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-slate-500 dark:text-slate-400">Usage Rate</span>
                                                        <span className="text-slate-900 dark:text-white font-medium">${module.usage_rate}/unit</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-500 dark:text-slate-400">Pricing Model</span>
                                                    <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded">
                                                        {module.pricing_model}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            <div className="flex gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                                                <button className="flex-1 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                                                    Edit
                                                </button>
                                                <button className="flex-1 py-2 text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-500/10 rounded-lg transition-colors">
                                                    View Details
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Roles Tab */}
                        {activeTab === 'roles' && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Roles & Permissions</h2>
                                    <button className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors">
                                        <Plus className="w-4 h-4" /> Add Role
                                    </button>
                                </div>
                                
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {roles.map(role => (
                                        <div key={role.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                                                    <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                                </div>
                                                {role.is_system_role && (
                                                    <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs rounded">System</span>
                                                )}
                                            </div>
                                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{role.role_name}</h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{role.description}</p>
                                            
                                            <div className="mb-4">
                                                <p className="text-xs text-slate-500 mb-2">Permissions ({role.permissions?.length || 0})</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {role.permissions?.slice(0, 4).map(perm => (
                                                        <span key={perm} className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded">
                                                            {perm}
                                                        </span>
                                                    ))}
                                                    {(role.permissions?.length || 0) > 4 && (
                                                        <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs rounded">
                                                            +{role.permissions.length - 4} more
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <button className="w-full py-2 text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-500/10 rounded-lg transition-colors">
                                                Manage Permissions
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
            
            {/* Company Wizard Modal */}
            <CompanyWizard 
                isOpen={showCompanyWizard}
                onClose={() => setShowCompanyWizard(false)}
                onSuccess={fetchData}
            />
            
            {/* Add User Modal */}
            {showUserModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="user-modal">
                    <div className="bg-slate-900 rounded-xl border border-slate-700 p-6 w-full max-w-md mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-white">Add New User</h2>
                            <button onClick={() => setShowUserModal(false)} className="p-1 hover:bg-slate-700 rounded">
                                <X className="w-5 h-5 text-slate-400" />
                            </button>
                        </div>
                        
                        {modalError && (
                            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                                {modalError}
                            </div>
                        )}
                        
                        <form onSubmit={handleCreateUser} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">First Name *</label>
                                    <input
                                        type="text"
                                        value={userForm.first_name}
                                        onChange={(e) => setUserForm({...userForm, first_name: e.target.value})}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="John"
                                        required
                                        data-testid="user-firstname-input"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-slate-400 mb-1">Last Name *</label>
                                    <input
                                        type="text"
                                        value={userForm.last_name}
                                        onChange={(e) => setUserForm({...userForm, last_name: e.target.value})}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="Doe"
                                        required
                                        data-testid="user-lastname-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm text-slate-400 mb-1">Email *</label>
                                <input
                                    type="email"
                                    value={userForm.email}
                                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    placeholder="john@company.com"
                                    required
                                    data-testid="user-email-input"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-slate-400 mb-1">Password *</label>
                                <input
                                    type="password"
                                    value={userForm.password}
                                    onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    placeholder="Min 8 characters"
                                    required
                                    minLength={8}
                                    data-testid="user-password-input"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-slate-400 mb-1">Company *</label>
                                <select
                                    value={userForm.company_id}
                                    onChange={(e) => setUserForm({...userForm, company_id: e.target.value})}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    required
                                    data-testid="user-company-select"
                                >
                                    <option value="">Select Company</option>
                                    {companies.map(c => (
                                        <option key={c.id} value={c.id}>{c.company_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm text-slate-400 mb-1">Role *</label>
                                <select
                                    value={userForm.role_id}
                                    onChange={(e) => setUserForm({...userForm, role_id: e.target.value})}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    required
                                    data-testid="user-role-select"
                                >
                                    <option value="">Select Role</option>
                                    {roles.map(r => (
                                        <option key={r.id} value={r.id}>{r.role_name}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowUserModal(false)}
                                    className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                                    data-testid="user-cancel-button"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={modalLoading}
                                    className="flex-1 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                    data-testid="user-submit-button"
                                >
                                    {modalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                    Create
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend, trendLabel, color, testId, date }) => {
    const colorClasses = {
        teal: 'bg-teal-500/20 text-teal-600 dark:text-teal-400',
        blue: 'bg-blue-500/20 text-blue-600 dark:text-blue-400',
        emerald: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400',
        amber: 'bg-amber-500/20 text-amber-600 dark:text-amber-400',
        purple: 'bg-purple-500/20 text-purple-600 dark:text-purple-400'
    };
    
    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6" data-testid={testId}>
            <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
                    <Icon className="w-6 h-6" />
                </div>
                <div className="flex flex-col items-end gap-1">
                    {trend !== undefined && (
                        <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-sm">
                            <TrendingUp className="w-4 h-4" />
                            <span>+{trend}</span>
                        </div>
                    )}
                    {date && (
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {date}
                        </span>
                    )}
                </div>
            </div>
            <p className="text-3xl font-bold text-slate-900 dark:text-white mb-1" data-testid={`${testId}-value`}>{value}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
            {trendLabel && <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{trendLabel}</p>}
        </div>
    );
};

export default AdminPanel;
